import type Guid from "@/app/types/common/guid";
import { containerService } from "@/app/services/extensions/containerServiceExtended";
import { productBatchService } from "@/app/services/generated/productbatchService";
import appStore from "@/store/modules/appStore";
import type { PackageType } from "@/app/services/generated/packagetypeService";
import { translate, keys } from "@/plugins/i18n";
import { showModalDialog } from "@/components/platform/common/showModalDialog";
import type { ContainerWithStatus } from "@/app/services/generated/containerService";
import FillContainersDialog from "@/components/pages/containers/dialogs/FillContainersDialog.vue";
import type { Item } from "@/app/services/generated/itemService";

export interface FillContainersEvent {
  permLabelsToAdd: string[];
  permLabelsToRemove: string[];
  stockItemId: Guid;
  urn: string;
}

export interface FillContainersDialogProps {
  filledContainers?: ContainerWithStatus[];
  readOnly?: boolean;
  stockItemId?: Guid;
  batchUrn?: string;
  isAssembly?: boolean;
}

export const showFillContainersDialog = async (
  fillContainersProps: FillContainersDialogProps
) => {
  return await showModalDialog<
    FillContainersEvent,
    typeof FillContainersDialog
  >(FillContainersDialog, {
    props: {
      ...fillContainersProps,
    },
  });
};

export const isItemRackable = (
  item: Item,
  packageType?: Readonly<PackageType>
) => {
  if (item.details.mainPurpose.isMaterial()) {
    return false;
  }
  return !!packageType?.isReturnable;
};

export const updateContainers = async (
  fillContainersEvent: FillContainersEvent,
  productBatchId?: Guid,
  assemblyId?: Guid
) => {
  await emptyContainers(fillContainersEvent.permLabelsToRemove);
  await fillContainers(
    fillContainersEvent.permLabelsToAdd,
    fillContainersEvent.stockItemId,
    fillContainersEvent.urn,
    productBatchId,
    assemblyId
  );
};

/**
 *
 * @param permLabels List of perm labels to fill
 * @param stockItemId Product to fill the containers with
 * @param batchUrn Batch of the product to fill the containers with
 * @param productBatchId Optional product batch id (overrides batchUrn)
 * @param assemblyId Optional assembly id
 */
export const fillContainers = async (
  permLabels: string[],
  stockItemId: Guid,
  batchUrn: string,
  productBatchId?: Guid,
  assemblyId?: Guid
) => {
  if (!permLabels.length) {
    // Nothing to update
    return;
  }

  if (!productBatchId) {
    const productBatch = await productBatchService.getOrCreateProductBatch(
      stockItemId,
      batchUrn
    );
    productBatchId = productBatch.id;
  }

  await containerService.fillContainers(
    permLabels,
    productBatchId,
    assemblyId ?? null,
    null
  );

  showContainerUpdateSnackbar(permLabels, "fill");
};

export const emptyContainers = async (permLabels: readonly string[]) => {
  await containerService.emptyContainers(permLabels);
  showContainerUpdateSnackbar(permLabels, "empty");
};

const showContainerUpdateSnackbar = (
  permLabels: readonly string[],
  updateAction: "fill" | "empty"
) => {
  if (!permLabels.length) {
    return;
  }
  const permLabelText =
    permLabels[0] +
    (permLabels.length > 1
      ? ` + ${permLabels.length - 1} ${translate(keys.more)}`
      : "");
  const actionKey =
    updateAction === "fill"
      ? keys.production_packaging_assembly_filled_container
      : keys.production_packaging_assembly_emptied_container;

  appStore.pushSnackbarConfig({
    text: translate(actionKey, {
      permLabel: permLabelText,
    }).toString(),
    timeout: 4000,
  });
};
